<template>
  <Transition name="fade">
    <div v-if="visible" class="start-mask">
      <div class="start-content">
        <!-- 标志区域 -->
        <div class="start-logo">
          <img src="/img/logo_aiiifly.png" alt="Aiiifly" class="logo-img" />
        </div>

        <!-- 初始状态 - 开始使用按钮 -->
        <div v-if="connectionStage === 'initial'" class="start-tips">
          <div class="start-button" @click="handleStart">
            <img src="/img/startBtn_icon.png" alt="" class="start-btn-icon" />
            <span class="start-btn-text">{{ t('start.use') }}</span>
          </div>
          <p class="link-text">{{ t('start.tip') }}</p>
          <div v-if="showError" class="link-status-tips error">
            {{ errorMessage }}
          </div>
        </div>

        <!-- 连接中状态 -->
        <div v-else-if="connectionStage === 'requesting' || connectionStage === 'opening'"
             class="connecting-tips">
          <div class="loading-spinner"></div>
          <p class="connecting-text">{{ t('start.connecting') }}</p>
        </div>

        <!-- 等待2.4G配对 -->
        <div v-else-if="connectionStage === 'waiting_pairing'" class="waiting-tips">
          <div class="pulse-icon">📡</div>
          <p class="waiting-title">{{ t('start.waitingPairing') }}</p>
          <p class="waiting-subtitle">{{ t('start.pairingHint') }}</p>
        </div>

        <!-- 等待USB连接 -->
        <div v-else-if="connectionStage === 'waiting_usb'" class="waiting-tips">
          <div class="pulse-icon">🔌</div>
          <p class="waiting-title">{{ t('start.waitingUSB') }}</p>
          <p class="waiting-subtitle">{{ t('start.usbHint') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="connectionStage === 'error'" class="error-tips">
          <div class="error-icon">⚠️</div>
          <p class="error-message">{{ errorMessage }}</p>
          <button @click="handleRetry" class="retry-button">
            {{ t('start.retry') }}
          </button>
        </div>

        <!-- 备案信息（可选，根据需要显示） -->
        <div v-if="showBeian" class="beian">
          <a href="https://beian.miit.gov.cn/" target="_blank">
            粤ICP备2025456694号
          </a>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useDeviceStore } from '@/stores/device'
import { useI18n } from 'vue-i18n'

const deviceStore = useDeviceStore()
const { t } = useI18n()

// 是否显示遮罩
const eqPreview = import.meta.env.DEV
  && new URLSearchParams(window.location.search).has('eqPreview')
const visible = computed(() => !eqPreview && !deviceStore.isDeviceConnected)

// 连接阶段
const connectionStage = computed(() => deviceStore.connectionStage)

// 错误信息
const errorMessage = computed(() => deviceStore.error || t('start.errorDefault'))

// 是否显示错误（在初始状态下）
const showError = computed(() =>
  connectionStage.value === 'initial' && deviceStore.error
)

// 是否显示备案信息（中国地区）
const showBeian = computed(() => {
  // 可以根据用户语言或地区判断
  return false // 默认不显示，根据需求调整
})

// 处理开始按钮点击
async function handleStart() {
  try {
    await deviceStore.connect()
  } catch (error) {
    console.error('连接失败:', error)
  }
}

// 处理重试
async function handleRetry() {
  try {
    await deviceStore.connect()
  } catch (error) {
    console.error('重试失败:', error)
  }
}
</script>

<style scoped>
.start-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: #0a0a0a;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}

.start-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
}

/* 标志 */
.start-logo {
  animation: fadeInDown 0.8s ease-out;
}

.logo-img {
  width: 180px;
  height: auto;
}

/* 初始提示 */
.start-tips {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

.start-button {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.start-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
}

.start-button:active {
  transform: translateY(0);
}

.start-btn-icon {
  width: 24px;
  height: 24px;
}

.start-btn-text {
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.link-text {
  color: #a0a0a0;
  font-size: 14px;
  margin: 0;
  text-align: center;
}

.link-status-tips {
  padding: 12px 20px;
  background: rgba(234, 118, 24, 0.1);
  border: 1px solid rgba(234, 118, 24, 0.3);
  border-radius: 8px;
  color: #ea7618;
  font-size: 13px;
  text-align: center;
  max-width: 300px;
}

/* 连接中状态 */
.connecting-tips {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  animation: fadeIn 0.5s ease-out;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(102, 126, 234, 0.2);
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.connecting-text {
  color: #ffffff;
  font-size: 16px;
  margin: 0;
}

/* 等待状态 */
.waiting-tips {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  animation: fadeIn 0.5s ease-out;
}

.pulse-icon {
  font-size: 60px;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
}

.waiting-title {
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.waiting-subtitle {
  color: #a0a0a0;
  font-size: 14px;
  margin: 0;
  text-align: center;
  max-width: 300px;
}

/* 错误状态 */
.error-tips {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  animation: fadeIn 0.5s ease-out;
}

.error-icon {
  font-size: 60px;
  animation: shake 0.5s ease-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  75% { transform: translateX(10px); }
}

.error-message {
  color: #ff6b6b;
  font-size: 16px;
  margin: 0;
  text-align: center;
  max-width: 350px;
}

.retry-button {
  padding: 12px 32px;
  background: #667eea;
  border: none;
  border-radius: 25px;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.retry-button:hover {
  background: #5568d3;
  transform: translateY(-2px);
}

.retry-button:active {
  transform: translateY(0);
}

/* 备案信息 */
.beian {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
}

.beian a {
  color: #666;
  font-size: 12px;
  text-decoration: none;
  transition: color 0.3s ease;
}

.beian a:hover {
  color: #999;
}

/* 动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .start-content {
    gap: 30px;
    padding: 20px;
  }

  .logo-img {
    width: 150px;
  }

  .start-button {
    padding: 14px 28px;
  }

  .start-btn-text {
    font-size: 16px;
  }

  .pulse-icon {
    font-size: 50px;
  }

  .waiting-title,
  .connecting-text,
  .error-message {
    font-size: 15px;
  }
}
</style>
